import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import {
  fetchCampaignDetailsAction,
  clearCampaignDetails,
} from "@/modules/stochastic/features/CampaignManagement/slices/CampaignDetailsSlice";
import { useCreateEditCampaign } from "./useCreateEditCampaign";

export function useCloneCampaign() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const hasAppliedFilters = useRef(false);
  const hasPrePopulated = useRef(false);
  const originalPostalCodes = useRef<
    { postalCode: string | number; limit: number }[]
  >([]);

  const { campaignDetails, loading: loadingOriginalCampaign } = useSelector(
    (state: RootState) => state.campaignDetails,
  );

  const createEditHook = useCreateEditCampaign();

  useEffect(() => {
    if (id) {
      // Reset refs when ID changes to allow re-population
      hasPrePopulated.current = false;
      hasAppliedFilters.current = false;
      originalPostalCodes.current = [];

      // Clear previous campaign data before fetching new one
      dispatch(clearCampaignDetails());
      dispatch(fetchCampaignDetailsAction({ campaignId: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    // Only populate if campaign details match the requested ID
    if (
      campaignDetails &&
      campaignDetails.id === Number(id) &&
      createEditHook.campaignProducts.length > 0 &&
      !hasPrePopulated.current
    ) {
      hasPrePopulated.current = true;
      const selectedProduct =
        createEditHook.campaignProducts.find(
          (p) => p.id === campaignDetails.campaignProduct?.id,
        );
      originalPostalCodes.current = campaignDetails.postalCodeLimits || [];

      createEditHook.setFormData((prev) => ({
        ...prev,
        campaignProductId: selectedProduct?.id.toString() ?? "",
        campaignName: `${campaignDetails.name} (Copy)`,
        description: campaignDetails.description || "",
        phoneNumber: campaignDetails.phoneNumber || "",
        startDate: "",
        endDate: "",
        mailingFrequency:
          campaignDetails.mailingSchedule?.mailingFrequency?.value?.toString() ||
          "",
        selectedMailingWeeks:
          campaignDetails.mailingSchedule?.mailingDropWeeks?.map(
            (week) => week.weekNumber,
          ) || [],
        locations:
          campaignDetails.locations
            ?.map((l) => parseInt(l.id || "0"))
            .filter((id) => id > 0) || [],
        filterCriteria: {
          audience:
            campaignDetails.filters?.campaignTarget?.value ||
            "include_prospects_only",
          addressType:
            campaignDetails.filters?.addressType?.value ||
            "include_residential_only",
          prospectAge: {
            min:
              campaignDetails.filters?.demographicTargets?.find(
                (t) => t.name === "Prospect Age Min",
              )?.value || "40",
            max:
              campaignDetails.filters?.demographicTargets?.find(
                (t) => t.name === "Prospect Age Max",
              )?.value || "90",
          },
          estimatedIncome:
            campaignDetails.filters?.demographicTargets?.find(
              (t) => t.name === "Estimated Income",
            )?.value || "",
          homeAge:
            campaignDetails.filters?.demographicTargets?.find(
              (t) => t.name === "Home Age",
            )?.value || "5",
          excludeClubMembers:
            campaignDetails.filters?.customerRestrictionCriteria?.some(
              (c) => c.value === "exclude_club_members",
            ) || false,
          excludeLTV:
            campaignDetails.filters?.customerRestrictionCriteria?.some(
              (c) => c.value === "5000",
            ) || false,
          excludeInstallCustomers:
            campaignDetails.filters?.customerRestrictionCriteria?.some(
              (c) => c.value === "exclude_customer_installations",
            ) || false,
        },
        tags: campaignDetails.filters?.tags?.map((t) => t.name).join(",") || "",
      }));

      if (!hasAppliedFilters.current) {
        hasAppliedFilters.current = true;
        setTimeout(() => {
          createEditHook.applyFilters();
        }, 100);
      }
    }
  }, [campaignDetails, createEditHook.campaignProducts, id]);

  useEffect(() => {
    if (
      createEditHook.formData.zipCodes.length > 0 &&
      originalPostalCodes.current.length > 0 &&
      hasAppliedFilters.current
    ) {
      const originalSelections = new Map(
        originalPostalCodes.current.map((pc) => [
          pc.postalCode.toString(),
          pc.limit,
        ]),
      );

      const updatedZipCodes = createEditHook.formData.zipCodes.map(
        (zipCode) => {
          const originalLimit = originalSelections.get(zipCode.code);

          if (originalLimit !== undefined) {
            const selectedProspects = Math.min(
              originalLimit,
              zipCode.filteredProspects,
            ).toString();

            return {
              ...zipCode,
              selectedProspects,
            };
          }

          return zipCode;
        },
      );

      createEditHook.setFormData((prev) => ({
        ...prev,
        zipCodes: updatedZipCodes,
      }));

      originalPostalCodes.current = [];
    }
  }, [createEditHook.formData.zipCodes]);

  return {
    ...createEditHook,
    loadingOriginalCampaign,
  };
}
